import React, { ComponentProps, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import {
  pointerEventsStyles,
  BodyText,
  CloudinaryImage,
  ContentfulImage,
  Icon,
  Image,
  LoadingDots,
  theme,
  useResponsive,
} from '@sg/garnish';
import { AllergenProperty } from '@sg/graphql-schema';
import { useDietaryRestrictions, type AllergenTypes } from '@sg/shared-fe-features-menu';

import { HomeProductCardPressable } from '../HomeProductCardPressable';
import { HomeProductNutritionData } from '../HomeProductNutritionData';

// ────────────────────────────────────────────────────────────────────────────

export const HomeProductCard = (props: HomeProductCardProps) => {
  const {
    categoryName,
    isEnriched,
    id,
    slug,
    isModifiable,
    title = '',
    description,
    deepLink,
    cost,
    fallbackImage,
    cloudinaryImage,
    contentfulImage,
    imageA11y,
    dietaryPreferences,
    allergenProperties,
    calories,
    calorieOverrideText,
    withMaxWidth = false,
    openMenu,
    openProductDetails,
    addProductToBag,
    trackItemSelection,
  } = props;

  const { formatNumber } = useIntl();
  const { match } = useResponsive();

  // ─── Styles ────────────────────────────────────────────────────────────────

  const cloudinaryImageConfig = match([CLOUDINARY_IMAGE_CONFIG_XS, CLOUDINARY_IMAGE_CONFIG_SM, CLOUDINARY_IMAGE_CONFIG_LG, CLOUDINARY_IMAGE_CONFIG_XL]); // prettier-ignore
  const contentfulImageConfig = match([CONTENTFUL_IMAGE_CONFIG_XS, CONTENTFUL_IMAGE_CONFIG_SM, CONTENTFUL_IMAGE_CONFIG_LG, CONTENTFUL_IMAGE_CONFIG_XL]); // prettier-ignore
  const maxWidthStyle = match([styles.maxWidthXS, styles.maxWidthSM, styles.maxWidthLG, styles.maxWidthXL]); // prettier-ignore
  const containerStyle = match([styles.containerXS, styles.containerSM, styles.containerLG, styles.containerXL]); // prettier-ignore
  const imageStyle = match([styles.imageXS, styles.imageSM, styles.imageLG, styles.imageXL]);
  const widthStyle = withMaxWidth ? maxWidthStyle : null;

  // ─── Restriction Notice ────────────────────────────────────────────────────

  const getRestrictionNotice = useDietaryRestrictions({ restrictions: dietaryPreferences });
  const restrictionNotice = getRestrictionNotice({ allergenProperties });

  // ─── State ─────────────────────────────────────────────────────────────────

  const [isAddingToBag, setIsAddingToBag] = useState(false);

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const handleLinkPress = useCallback(() => {
    trackItemSelection(title, categoryName);
  }, [categoryName, title, trackItemSelection]);

  const handlePress = useCallback(async () => {
    trackItemSelection(title, categoryName);

    // Products from contentful that aren't enriched with a menu yet open the location selection instead.
    if (!isEnriched) {
      openMenu();
      return;
    }

    if (isModifiable) {
      openProductDetails(slug);
      return;
    }

    setIsAddingToBag(true);
    await addProductToBag(id, title);
    setIsAddingToBag(false);
  }, [
    isEnriched,
    categoryName,
    id,
    slug,
    title,
    isModifiable,
    openProductDetails,
    addProductToBag,
    openMenu,
    trackItemSelection,
  ]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <HomeProductCardPressable
      deepLink={deepLink}
      style={[containerStyle, widthStyle]}
      onPress={deepLink ? handleLinkPress : handlePress}
    >
      <View style={styles.imageContainer}>
        {cloudinaryImage ? (
          <CloudinaryImage
            contentFit="contain"
            baseUrl={cloudinaryImage}
            config={cloudinaryImageConfig}
            aria-label={imageA11y ?? title}
            style={imageStyle}
          />
        ) : null}

        {contentfulImage && !cloudinaryImage ? (
          <ContentfulImage
            contentFit="contain"
            baseUrl={contentfulImage}
            options={contentfulImageConfig}
            aria-label={imageA11y ?? title}
            style={imageStyle}
          />
        ) : null}

        {!cloudinaryImage && !contentfulImage && fallbackImage ? (
          <Image
            contentFit="contain"
            source={fallbackImage}
            aria-label={imageA11y ?? title}
            style={imageStyle}
          />
        ) : null}

        {isModifiable || !cost ? null : (
          <View style={[styles.addToBagIcon, pointerEventsStyles.none]}>
            {isAddingToBag ? (
              <LoadingDots color={theme.colors.DARK_KALE} />
            ) : (
              <Icon
                name="IconPlus"
                width={ADD_ICON_SIZE}
                height={ADD_ICON_SIZE}
                color={theme.colors.DARK_KALE}
              />
            )}
          </View>
        )}
      </View>

      {title ? (
        <BodyText style={match([styles.titleXS, styles.titleSM])} sizeMatch={['14', '18']} bold>
          {title}
        </BodyText>
      ) : null}

      {description ? (
        <BodyText
          sizeMatch={['12', '14']}
          style={match([styles.descriptionXS, styles.descriptionSM])}
          numberOfLines={match([2, 3])}
        >
          {description}
        </BodyText>
      ) : null}

      {restrictionNotice ? (
        <BodyText sizeMatch={['12']} style={styles.restrictionNoticeText}>
          {restrictionNotice}
        </BodyText>
      ) : null}

      {hasNutritionData(props) ? <HomeProductNutritionData {...props} /> : null}

      {calories && calorieOverrideText ? (
        <BodyText
          sizeMatch={['12']}
          style={match([styles.calorieOverrideTextXS, styles.calorieOverrideTextSM])}
        >
          {calorieOverrideText}
        </BodyText>
      ) : null}

      {cost ? (
        <BodyText
          sizeMatch={['12']}
          style={match([styles.costXS, styles.costSM])}
          bold={match([false, true])}
        >
          {formatNumber(cost / 100, { style: 'currency', currency: 'USD' })}
        </BodyText>
      ) : null}
    </HomeProductCardPressable>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const hasNutritionData = (props: HomeProductCardProps) => {
  return (
    props.calories ||
    props.totalFatG ||
    props.totalCarbsG ||
    props.dietaryFiberG ||
    props.sugarsG ||
    props.proteinG ||
    props.co2eKg
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const IMAGE_SIZE_XS = 160;
const IMAGE_SIZE_SM = 270;
const IMAGE_SIZE_LG = 270;
const IMAGE_SIZE_XL = 365;

export const HOME_PRODUCT_CARD_SIZES = [IMAGE_SIZE_XS, IMAGE_SIZE_SM, IMAGE_SIZE_LG, IMAGE_SIZE_XL];

const ADD_ICON_SIZE = 24;
const ADD_BUTTON_SIZE = 32;

const SHARED_CLOUDINARY_OPTIONS = {
  crop: 'crop' as const,
  height: 0.7,
  width: 0.4,
};

const SHARED_CONTENTFUL_OPTIONS = {
  fm: 'webp' as const,
  fit: 'fill' as const,
  q: 70,
};

const CLOUDINARY_IMAGE_CONFIG_XS = [SHARED_CLOUDINARY_OPTIONS, { width: IMAGE_SIZE_XS }];
const CLOUDINARY_IMAGE_CONFIG_SM = [SHARED_CLOUDINARY_OPTIONS, { width: IMAGE_SIZE_SM }];
const CLOUDINARY_IMAGE_CONFIG_LG = [SHARED_CLOUDINARY_OPTIONS, { width: IMAGE_SIZE_LG }];
const CLOUDINARY_IMAGE_CONFIG_XL = [SHARED_CLOUDINARY_OPTIONS, { width: IMAGE_SIZE_XL }];

const CONTENTFUL_IMAGE_CONFIG_XS = { ...SHARED_CONTENTFUL_OPTIONS, w: IMAGE_SIZE_XS };
const CONTENTFUL_IMAGE_CONFIG_SM = { ...SHARED_CONTENTFUL_OPTIONS, w: IMAGE_SIZE_SM };
const CONTENTFUL_IMAGE_CONFIG_LG = { ...SHARED_CONTENTFUL_OPTIONS, w: IMAGE_SIZE_LG };
const CONTENTFUL_IMAGE_CONFIG_XL = { ...SHARED_CONTENTFUL_OPTIONS, w: IMAGE_SIZE_XL };

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    flex: 1,
    gap: theme.spacing['2'],
    minWidth: IMAGE_SIZE_XS,
  },
  containerSM: {
    flex: 1,
    gap: theme.spacing['2'],
    minWidth: IMAGE_SIZE_SM,
  },
  containerLG: {
    flex: 1,
    gap: theme.spacing['2'],
    minWidth: IMAGE_SIZE_LG,
  },
  containerXL: {
    flex: 1,
    gap: theme.spacing['2'],
    minWidth: IMAGE_SIZE_XL,
  },
  maxWidthXS: {
    maxWidth: IMAGE_SIZE_XS,
  },
  maxWidthSM: {
    maxWidth: IMAGE_SIZE_SM,
  },
  maxWidthLG: {
    maxWidth: IMAGE_SIZE_LG,
  },
  maxWidthXL: {
    maxWidth: IMAGE_SIZE_XL,
  },
  imageContainer: {
    alignItems: 'center',
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    borderRadius: theme.radius.medium,
  },
  imageXS: {
    width: IMAGE_SIZE_XS,
    height: IMAGE_SIZE_XS,
  },
  imageSM: {
    width: IMAGE_SIZE_SM,
    height: IMAGE_SIZE_SM,
  },
  imageLG: {
    width: IMAGE_SIZE_LG,
    height: IMAGE_SIZE_LG,
  },
  imageXL: {
    width: IMAGE_SIZE_XL,
    height: IMAGE_SIZE_XL,
  },
  titleXS: {
    textTransform: 'capitalize',
    paddingRight: theme.spacing['2'],
  },
  titleSM: {
    marginTop: theme.spacing['2'],
    textTransform: 'capitalize',
  },
  descriptionXS: {
    color: theme.colors.CHARCOAL,
    paddingRight: theme.spacing['2'],
  },
  descriptionSM: {
    color: theme.colors.OPACITY.DARK_KALE.DARKER,
  },
  costXS: {
    color: theme.colors.CHARCOAL,
  },
  costSM: {
    color: theme.colors.OPACITY.DARK_KALE.DARKER,
  },
  calorieOverrideTextXS: {
    color: theme.colors.CHARCOAL,
  },
  calorieOverrideTextSM: {
    alignSelf: 'flex-start',
    color: theme.colors.CHARCOAL,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    borderRadius: theme.radius.large,
    paddingVertical: theme.spacing['1'],
    paddingHorizontal: theme.spacing['2'],
  },
  addToBagIcon: {
    alignItems: 'center',
    justifyContent: 'center',
    width: ADD_BUTTON_SIZE,
    height: ADD_BUTTON_SIZE,
    borderRadius: ADD_BUTTON_SIZE,
    backgroundColor: theme.colors.WHITE,
    position: 'absolute',
    bottom: theme.spacing['2'],
    right: theme.spacing['2'],
  },
  restrictionNoticeText: {
    color: theme.colors.CAUTION,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeProductCardProps = {
  categoryName: string;
  isEnriched: boolean;
  id: string;
  slug: string;
  isModifiable: boolean;
  title?: string;
  description?: string;
  deepLink?: string;
  cost?: number;
  fallbackImage?: ComponentProps<typeof Image>['source'];
  cloudinaryImage?: string;
  contentfulImage?: string;
  imageA11y?: string;
  dietaryPreferences: AllergenTypes;
  allergenProperties: readonly AllergenProperty[];
  calories?: number;
  calorieOverrideText?: string;
  totalFatG?: number;
  totalCarbsG?: number;
  dietaryFiberG?: number;
  sugarsG?: number;
  proteinG?: number;
  co2eKg?: number;
  withMaxWidth?: boolean;
  openMenu: () => void;
  openProductDetails: (slug: string) => void;
  addProductToBag: (id: string, title: string) => Promise<void>;
  trackItemSelection: (productId: string, categoryName: string) => void;
};
